import { isEmpty, isNil } from 'lodash';
import { useContext } from 'react';
import { Button } from '@shared/components/Button';
import { Text } from '@shared/components/Text';
import { ScrollView, View } from '@shared/components/View';
import { MBHover } from '@shared/components/hover';
import { MBIconV2 } from '@shared/components/image';
import { LoadingIndicator } from '@shared/components/loading';
import { MBSearchBar } from '@shared/components/searchBar';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { useListWorkflowTemplates } from '@shared/scenes/workflows/hooks';
import { isSubformComponent } from '@shared/scenes/workflows/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useDebouncedState, useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { AddSubformRow } from './AddSubformRow';

interface Props {
  workflowTemplate?: API.WorkflowTemplateFragment;
  onSelect: (subformWorkflowTemplate: API.WorkflowTemplateFragment) => void;
  navigateToAdd?: () => void;
}

export function SelectSubform({ workflowTemplate, onSelect, navigateToAdd }: Props) {
  const colors = useContext(ColorThemeContext);
  const isDesktop = useIsDesktop();
  const [query, setQuery] = useDebouncedState('', 300);

  const subformComponents = workflowTemplate?.components?.filter(isSubformComponent) ?? [];
  const workflowTemplateIds = subformComponents.map(c => c.workflowTemplateIds).flat();
  const { workflowTemplates, loading, hasMore, loadMore } = useListWorkflowTemplates({
    skip: isEmpty(workflowTemplateIds),
    input: { ids: workflowTemplateIds, name: isEmpty(query) ? undefined : query },
  });
  const hasAddSubformRow = !isNil(navigateToAdd) && isDesktop;

  return (
    <>
      <View paddingVertical={2} paddingHorizontal={2}>
        <MBSearchBar
          value={query}
          onChangeText={setQuery}
          placeholder={i18n('workflowTemplate.subformsSearch')}
        />
      </View>
      {loading && (
        <View center style={{ height: 150 }}>
          <LoadingIndicator />
        </View>
      )}
      {!loading && isEmpty(workflowTemplates) && (
        <View paddingBottom={1} centerHorizontally paddingHorizontal={2}>
          <View center padding={1}>
            <MBIconV2 name="list" color={colors.accent} size={grid(7)} />
          </View>
          <Text size="body1" align="center">
            {i18n('card.blocks.subformsInput.emptyState')}
          </Text>
        </View>
      )}
      <ScrollView>
        {workflowTemplates?.map(wft => (
          <MBTouchableOpacity key={wft.id} onPress={() => onSelect(wft)}>
            <MBHover hoverStyle={{ backgroundColor: colors.input.backgroundInactive }}>
              <View row key={wft.id} padding={1} paddingLeft={2} centerVertically>
                <MBIconV2 name="list" color={colors.foregroundActive} size={grid(3)} />
                <View paddingLeft={1} />
                <Text size="body1">{wft.name}</Text>
              </View>
            </MBHover>
          </MBTouchableOpacity>
        ))}
        {hasMore && (
          <View center paddingVertical={2}>
            <Button
              variant="tertiary"
              size="medium"
              label={i18n('card.list.showMore')}
              onClick={loadMore}
            />
          </View>
        )}
      </ScrollView>
      {hasAddSubformRow && <AddSubformRow onAdd={navigateToAdd} />}
      {!hasAddSubformRow && <View paddingTop={1} />}
    </>
  );
}
